<script setup lang="ts">
import { Users } from "@lucide/vue";
import { refDebounced } from "@vueuse/core";
import { computed, h, ref } from "vue";
import { RouterLink } from "vue-router";
import { usePrincipals } from "@/api/admin";
import type { Principal } from "@/api/contracts";
import AdminNav from "@/components/admin/AdminNav.vue";
import { accessTabs } from "@/components/admin/accessTabs";
import DataTable from "@/components/common/DataTable.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import { useSession } from "@/composables/useSession";
import { t } from "@/copy/strings";
import type { Column } from "@/lib/table";

definePage({ meta: { title: "People", requires: { adminArea: "users" } } });

const q = ref("");
const debounced = refDebounced(q, 250);
const principals = usePrincipals(debounced);

const { can } = useSession();
const tabs = computed(() => accessTabs(can));

const columns = computed<Column<Principal>[]>(() => [
  {
    id: "name",
    header: "Name",
    accessorFn: (p) => p.displayName || p.username,
    meta: { mobile: "title" },
    cell: ({ row }) =>
      h("div", { class: "min-w-0 whitespace-normal" }, [
        h("p", { class: "font-medium [overflow-wrap:anywhere]" }, row.original.displayName || row.original.username),
        h("p", { class: "text-xs font-normal text-muted-foreground [overflow-wrap:anywhere]" }, [row.original.username, row.original.email ? ` · ${row.original.email}` : ""]),
      ]),
  },
  { id: "kind", header: "Kind", accessorFn: (p) => p.kind, meta: { mobile: "meta" }, cell: ({ row }) => h("span", { class: "text-sm" }, row.original.kind) },
  {
    id: "groups",
    header: "Groups",
    accessorFn: (p) => (p.groups ?? []).join(", "),
    meta: { mobile: "meta" },
    cell: ({ row }) => h("span", { class: "text-xs whitespace-normal text-muted-foreground" }, (row.original.groups ?? []).join(", ") || "—"),
  },
  {
    id: "seen",
    header: "Last seen",
    accessorFn: (p) => p.lastSeenAt ?? "",
    meta: { mobile: "meta" },
    cell: ({ row }) => (row.original.lastSeenAt ? h(RelativeTime, { value: row.original.lastSeenAt, class: "text-sm whitespace-nowrap" }) : h("span", { class: "text-sm text-muted-foreground" }, "never")),
  },
  {
    id: "status",
    header: "Status",
    accessorFn: (p) => (p.disabled ? "disabled" : "active"),
    meta: { mobile: "badge" },
    cell: ({ row }) => (row.original.disabled ? h(StatusBadge, { status: "disabled" }) : null),
  },
  {
    id: "check",
    header: "",
    enableSorting: false,
    meta: { mobile: "actions", align: "end" },
    cell: ({ row }) =>
      h(
        RouterLink,
        {
          to: { path: "/admin/access/simulate", query: { user: row.original.username } },
          class: "inline-flex h-8 items-center rounded-md px-2 text-xs font-medium whitespace-nowrap text-primary transition-colors hover:bg-state-hover active:bg-state-press",
        },
        () => "Check access",
      ),
  },
]);
</script>

<template>
  <div>
    <PageHeader :title="t.nav.access" description="Everyone who has used Smake, with their Keycloak groups." :crumbs="[{ label: t.admin.title, to: '/admin' }]">
      <template #tabs><AdminNav :items="tabs" :label="t.nav.access" /></template>
    </PageHeader>
    <FilterBar v-model:search="q" placeholder="Search by name or email…" label="Filter people" class="mb-5" />
    <ListSkeleton v-if="principals.isPending.value" :rows="6" />
    <ErrorState v-else-if="principals.error.value" :error="principals.error.value" @retry="principals.refetch()" />
    <EmptyState v-else-if="!principals.data.value?.length" :icon="Users" :title="t.common.noResults" />
    <DataTable v-else :data="principals.data.value" :columns="columns" :row-key="(p) => p.id" caption="People who used Smake" />
  </div>
</template>
